/**
 * 영상 렌디션 고르기(site-media-delivery) — 보이는 폭 × 화면 픽셀 비율을 덮는 가장 작은 렌디션 하나.
 * 휴대폰·메모리 4GB 이하·데이터 절약·2g/3g 는 가장 작은 렌디션(H.264 Level 4.0, 모든 기기 하드웨어 디코딩).
 */
export interface RenditionContext {
  /** 영상이 그려질 폭(CSS px) */
  displayWidth: number;
  devicePixelRatio: number;
  /** 너비 900px 이하 화면이거나 모바일 기기 */
  isSmallDevice: boolean;
  /** 기기 메모리(GB, 모르면 undefined) */
  deviceMemoryGb?: number;
  saveData: boolean;
  /** Network Information API 의 effectiveType */
  effectiveType?: string;
}

/** 넉넉히 덮는다고 보는 비율 — 필요 화소의 95% 이상이면 그 렌디션으로 충분하다. */
const COVER_RATIO = 0.95;
const LOW_MEMORY_GB = 4;
const SLOW_CONNECTION = /^(slow-)?2g$|^3g$/;

export function isConstrainedDevice(context: RenditionContext): boolean {
  const slow =
    context.saveData || SLOW_CONNECTION.test(context.effectiveType ?? "");
  const lowMemory =
    context.deviceMemoryGb !== undefined &&
    context.deviceMemoryGb <= LOW_MEMORY_GB;
  return slow || context.isSmallDevice || lowMemory;
}

/** `widths` 는 오름차순. */
export function pickRenditionWidth(
  widths: readonly number[],
  context: RenditionContext
): number {
  if (widths.length === 0) throw new Error("렌디션이 없습니다");
  if (isConstrainedDevice(context)) return widths[0];
  const need = context.displayWidth * context.devicePixelRatio;
  return (
    widths.find((width) => width >= need * COVER_RATIO) ??
    widths[widths.length - 1]
  );
}

/** 고른 렌디션 폭 이상인 가장 작은 포스터, 없으면 가장 큰 포스터. */
export function pickPosterWidth(
  posterWidths: readonly number[],
  renditionWidth: number
): number | undefined {
  return (
    posterWidths.find((width) => width >= renditionWidth) ??
    posterWidths[posterWidths.length - 1]
  );
}

const MOBILE_USER_AGENT = /Android|iPhone|iPad|iPod|Mobile/i;
const SMALL_SCREEN_QUERY = "(max-width: 900px)";

interface NavigatorWithHints extends Navigator {
  connection?: { saveData?: boolean; effectiveType?: string };
  deviceMemory?: number;
}

/** 브라우저에서 고르기 문맥을 읽는다. */
export function readRenditionContext(displayWidth: number): RenditionContext {
  const nav = navigator as NavigatorWithHints;
  return {
    displayWidth: displayWidth || window.innerWidth,
    devicePixelRatio: window.devicePixelRatio || 1,
    isSmallDevice:
      window.matchMedia(SMALL_SCREEN_QUERY).matches ||
      MOBILE_USER_AGENT.test(nav.userAgent),
    deviceMemoryGb: nav.deviceMemory,
    saveData: Boolean(nav.connection?.saveData),
    effectiveType: nav.connection?.effectiveType,
  };
}
